import React, { useState, useEffect, useCallback } from 'react';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FunnelIcon, 
  BookmarkIcon, 
  ArrowsUpDownIcon,
  XMarkIcon,
  PlusIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { signalDescriptions, rankDescriptions } from '../constants/signalDescriptions';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import DisclaimerFooter from './DisclaimerFooter';
import { generateDataTablePDF, formatPDFDate, formatPDFNumber, formatPDFCurrency } from '../utils/pdfExport';

export default function ResearchPage() {
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [isApproved, setIsApproved] = useState(false);
  const [filters, setFilters] = useState({
    market_cap: { min: '', max: '' },
    monthly_signal: { min: '', max: '' },
    value_score: { min: '', max: '' },
    momentum_score: { min: '', max: '' },
    quality_score: { min: '', max: '' },
    lowvol_score: { min: '', max: '' },
    earnings_score: { min: '', max: '' },
    transcripts_score: { min: '', max: '' },
    innovation_score: { min: '', max: '' },
    sectors: [],
    rank_range: { min: '', max: '' },
    rank_change: { min: '', max: '' }
  });
  const [sortConfig, setSortConfig] = useState({ key: 'filter_rank', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [publicFilters, setPublicFilters] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tickerSearch, setTickerSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const navigate = useNavigate();

  // Available sectors
  const availableSectors = [
    'Commercial Services',
    'Communications', 
    'Consumer Discretionary',
    'Consumer Non-Durables',
    'Energy',
    'Finance',
    'Healthcare',
    'Industrials',
    'Materials',
    'Miscellaneous',
    'Technology',
    'Transportation',
    'Utilities'
  ];

  const fetchStockData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Build query parameters for research endpoint
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', pageSize);
      
      // Add filter criteria
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'sectors' && value.length > 0) {
          params.append('sectors', value.join(','));
        } else if (typeof value === 'object') {
          if (value.min !== '' && value.min !== undefined && value.min !== null) {
            params.append(`${key}_min`, value.min);
          }
          if (value.max !== '' && value.max !== undefined && value.max !== null) {
            params.append(`${key}_max`, value.max);
          }
        }
      });
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/research/stocks?${params.toString()}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );
      
      // Backend should return: { data: [...], total_count: number, page: number, has_more: boolean }
      setTotalCount(response.data.total_count || 0);
      
      // Add filter ranking (1, 2, 3...) based on current page
      const dataWithFilterRank = (response.data.data || []).map((stock, index) => ({
        ...stock,
        filter_rank: ((currentPage - 1) * pageSize) + index + 1
      }));
      
      setFilteredData(dataWithFilterRank);
    } catch (error) {
      console.error('Error fetching stock data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, currentPage, pageSize, filters]);

  const fetchSavedFilters = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/research/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedFilters(response.data.user_filters || []);
      setPublicFilters(response.data.public_filters || []);
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      setSavedFilters([]);
      setPublicFilters([]);
    }
  }, []);

  const handleExportPDF = async () => {
    if (!filteredData || filteredData.length === 0 || !isApproved) return;

    // Prepare filter summary
    const activeFilters = {};
    if (filters.market_cap.min || filters.market_cap.max) {
      activeFilters['Market Cap'] = `${filters.market_cap.min || 'Any'} - ${filters.market_cap.max || 'Any'}M`;
    }
    if (filters.monthly_signal.min || filters.monthly_signal.max) {
      activeFilters['SM Signal'] = `${filters.monthly_signal.min || 'Any'} - ${filters.monthly_signal.max || 'Any'}`;
    }
    if (filters.value_score.min || filters.value_score.max) {
      activeFilters['Value Score'] = `${filters.value_score.min || 'Any'} - ${filters.value_score.max || 'Any'}`;
    }
    if (filters.momentum_score.min || filters.momentum_score.max) {
      activeFilters['Momentum Score'] = `${filters.momentum_score.min || 'Any'} - ${filters.momentum_score.max || 'Any'}`;
    }
    if (filters.quality_score.min || filters.quality_score.max) {
      activeFilters['Quality Score'] = `${filters.quality_score.min || 'Any'} - ${filters.quality_score.max || 'Any'}`;
    }
    if (filters.lowvol_score.min || filters.lowvol_score.max) {
      activeFilters['Low Vol Score'] = `${filters.lowvol_score.min || 'Any'} - ${filters.lowvol_score.max || 'Any'}`;
    }
    if (filters.sectors && filters.sectors.length > 0) {
      activeFilters['Sectors'] = filters.sectors.join(', ');
    }
    if (filters.rank_range.min || filters.rank_range.max) {
      activeFilters['Rank Range'] = `${filters.rank_range.min || 'Any'} - ${filters.rank_range.max || 'Any'}`;
    }

    // Prepare data for PDF - market_cap_usd is returned in actual USD, format it correctly
    const pdfData = filteredData.map(stock => [
      stock.filter_rank || 'N/A',
      stock.ticker || 'N/A',
      stock.name || 'N/A',
      stock.sector_name || 'N/A',
      (stock.market_cap_usd !== null && stock.market_cap_usd !== undefined) ? formatPDFCurrency(stock.market_cap_usd) : 'N/A',
      (stock.monthly_signal !== null && stock.monthly_signal !== undefined) ? formatPDFNumber(stock.monthly_signal, 2) : 'N/A',
      (stock.value_score !== null && stock.value_score !== undefined) ? formatPDFNumber(stock.value_score, 2) : 'N/A',
      (stock.momentum_score !== null && stock.momentum_score !== undefined) ? formatPDFNumber(stock.momentum_score, 2) : 'N/A',
      (stock.quality_score !== null && stock.quality_score !== undefined) ? formatPDFNumber(stock.quality_score, 2) : 'N/A',
      (stock.lowvol_score !== null && stock.lowvol_score !== undefined) ? formatPDFNumber(stock.lowvol_score, 2) : 'N/A'
    ]);

    await generateDataTablePDF({
      title: 'Research Results',
      subtitle: `${totalCount} stocks matching your criteria (showing first ${filteredData.length})`,
      columns: ['Rank', 'Ticker', 'Name', 'Sector', 'Market Cap', 'SM Signal', 'Value', 'Momentum', 'Quality', 'Low Vol'],
      data: pdfData,
      filename: `SharpeMetrix_Research_${new Date().toISOString().split('T')[0]}.pdf`,
      filters: activeFilters,
      dateLabel: 'Exported',
      date: formatPDFDate(new Date()),
      tableStyles: {
        fontSize: 8,
        cellPadding: 2
      }
    });
  };

  // Check user permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsApproved(response.data.is_approved === true);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setIsApproved(false);
      }
    };
    checkPermissions();
  }, []);

  // Debounced effect for filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStockData();
    }, 800); // 800ms delay

    return () => clearTimeout(timeoutId);
  }, [fetchStockData]);

  // Initial load for saved filters (only once)
  useEffect(() => {
    fetchSavedFilters();
  }, [fetchSavedFilters]);

  // Reset to page 1 when filters change (immediate, no debounce needed)
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleFilterChange = (category, field, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleSectorToggle = (sector) => {
    setFilters(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const clearFilters = () => {
    setFilters({
      market_cap: { min: '', max: '' },
      monthly_signal: { min: '', max: '' },
      value_score: { min: '', max: '' },
      momentum_score: { min: '', max: '' },
      quality_score: { min: '', max: '' },
      lowvol_score: { min: '', max: '' },
      earnings_score: { min: '', max: '' },
      transcripts_score: { min: '', max: '' },
      innovation_score: { min: '', max: '' },
      sectors: [],
      rank_range: { min: '', max: '' },
      rank_change: { min: '', max: '' }
    });
  };

  const saveFilter = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/research/filters`, {
        filter_name: filterName,
        description: filterDescription,
        filter_criteria: filters
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowSaveModal(false);
      setFilterName('');
      setFilterDescription('');
      fetchSavedFilters();
    } catch (error) {
      console.error('Error saving filter:', error);
      alert('Error saving filter: ' + (error.response?.data?.error || error.message));
    }
  };

  const loadFilter = (filterCriteria) => {
    // Create a default filter structure to ensure all properties exist
    const defaultFilters = {
      market_cap: { min: '', max: '' },
      monthly_signal: { min: '', max: '' },
      value_score: { min: '', max: '' },
      momentum_score: { min: '', max: '' },
      quality_score: { min: '', max: '' },
      lowvol_score: { min: '', max: '' },
      earnings_score: { min: '', max: '' },
      transcripts_score: { min: '', max: '' },
      innovation_score: { min: '', max: '' },
      sectors: [],
      rank_range: { min: '', max: '' },
      rank_change: { min: '', max: '' }
    };

    // Merge the saved criteria with defaults, ensuring all nested objects exist
    const mergedFilters = { ...defaultFilters };
    
    if (filterCriteria) {
      Object.keys(filterCriteria).forEach(key => {
        if (key === 'sectors' && Array.isArray(filterCriteria[key])) {
          mergedFilters[key] = filterCriteria[key];
        } else if (typeof filterCriteria[key] === 'object' && filterCriteria[key] !== null) {
          mergedFilters[key] = {
            ...defaultFilters[key],
            ...filterCriteria[key]
          };
        }
      });
    }

    setFilters(mergedFilters);
    setShowLoadModal(false);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    let sortableData = [...filteredData];
    
    // Filter by global ticker search first (searches all data)
    if (tickerSearch && tickerSearch.trim() !== '') {
      sortableData = sortableData.filter(stock => 
        stock.ticker && stock.ticker.toUpperCase().includes(tickerSearch.toUpperCase())
      );
    }
    
    // Then sort
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle string sorting for ticker and name
        if (sortConfig.key === 'ticker' || sortConfig.key === 'name') {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
          
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        }
        
        // Handle numeric sorting
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    // Finally, filter by table search (searches only current results)
    if (tableSearch && tableSearch.trim() !== '') {
      sortableData = sortableData.filter(stock => 
        (stock.ticker && stock.ticker.toUpperCase().includes(tableSearch.toUpperCase())) ||
        (stock.name && stock.name.toUpperCase().includes(tableSearch.toUpperCase()))
      );
    }
    
    return sortableData;
  }, [filteredData, sortConfig, tickerSearch, tableSearch]);

  const formatMarketCap = (value) => {
    if (!value) return '–';
    // Value is already in millions from the API
    if (value >= 1000) {
      const billions = value / 1000;
      return `$${billions.toFixed(1)}B`;
    } else {
      return `$${value.toFixed(0)}M`;
    }
  };

  const formatScore = (value) => {
    if (value === null || value === undefined) return <span className="text-text-300">–</span>;
    return Math.round(parseFloat(value)).toString();
  };

  // Toggle tooltip function
  const toggleTooltip = (factor) => {
    if (activeTooltip === factor) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(factor);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Subscription banner for unapproved users
  const SubscriptionBanner = () => {
    if (isApproved) return null;
    
    return (
      <div className="mt-4 mb-6 bg-gradient-to-r from-primary-100/20 to-accent-100/20 rounded-lg p-6">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0 md:mr-6">
            <h3 className="text-lg font-semibold text-text-100">
              <LockClosedIcon className="h-5 w-5 inline-block mr-2" />
              Unlock Full Access to Research Tools
            </h3>
            <p className="mt-1 text-text-200">
              Get advanced filtering, saved searches, and full access to our comprehensive stock research platform.
            </p>
          </div>
          <div>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center rounded-md bg-primary-100 px-4 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
            >
              Request Full Access
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEO title="Research" description="Deep-dive into any ticker or ETF with institutional-grade factor and signal data." />
      <div className="flex-grow bg-bg-100 px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-text-100">Research</h1>
          <p className="mt-2 text-sm text-text-200">
            Comprehensive research and filtering across our entire universe of stocks
          </p>
        </div>
        
        {/* Ticker Search */}
        <div className="mt-4 sm:mt-0 sm:ml-4 min-w-[250px]">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-text-300" aria-hidden="true" />
            </div>
            <input
              type="text"
              placeholder="Search by ticker..."
              value={tickerSearch}
              onChange={(e) => setTickerSearch(e.target.value.toUpperCase())}
              className="block w-full rounded-md border-0 bg-bg-200 py-2 pl-10 pr-3 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-inset focus:ring-primary-100 sm:text-sm sm:leading-6"
            />
            {tickerSearch && (
              <button
                onClick={() => setTickerSearch('')}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <XMarkIcon className="h-4 w-4 text-text-300 hover:text-text-100" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 sm:mt-0 sm:ml-4 sm:flex-none space-x-2">
          <button
            onClick={handleExportPDF}
            disabled={!isApproved || filteredData.length === 0}
            className="inline-flex items-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Export PDF
          </button>
          <button
            onClick={() => setShowLoadModal(true)}
            disabled={!isApproved}
            className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BookmarkIcon className="h-4 w-4 mr-2" />
            Load Filter
          </button>
          <button
            onClick={() => setShowSaveModal(true)}
            disabled={!isApproved}
            className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Save Filter
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            disabled={!isApproved}
            className="inline-flex items-center rounded-md bg-bg-300 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-bg-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>
      </div>

      <SubscriptionBanner />

      {/* Filter Panel */}
      {isApproved && showFilters && (
        <div className="mt-6 bg-bg-200 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Market Cap Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Market Cap (Millions)</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.market_cap.min}
                  onChange={(e) => handleFilterChange('market_cap', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.market_cap.max}
                  onChange={(e) => handleFilterChange('market_cap', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Global Rank Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Global Rank Range</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.rank_range.min}
                  onChange={(e) => handleFilterChange('rank_range', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.rank_range.max}
                  onChange={(e) => handleFilterChange('rank_range', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Monthly Signal Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Overall Signal</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.monthly_signal.min}
                  onChange={(e) => handleFilterChange('monthly_signal', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.monthly_signal.max}
                  onChange={(e) => handleFilterChange('monthly_signal', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Value Score Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Value Score</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.value_score.min}
                  onChange={(e) => handleFilterChange('value_score', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.value_score.max}
                  onChange={(e) => handleFilterChange('value_score', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Momentum Score Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Momentum Score</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.momentum_score.min}
                  onChange={(e) => handleFilterChange('momentum_score', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.momentum_score.max}
                  onChange={(e) => handleFilterChange('momentum_score', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Quality Score Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Quality Score</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.quality_score.min}
                  onChange={(e) => handleFilterChange('quality_score', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.quality_score.max}
                  onChange={(e) => handleFilterChange('quality_score', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Low Vol Score Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Low Volatility Score</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.lowvol_score.min}
                  onChange={(e) => handleFilterChange('lowvol_score', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.lowvol_score.max}
                  onChange={(e) => handleFilterChange('lowvol_score', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Earnings Score Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Earnings Score</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.earnings_score.min}
                  onChange={(e) => handleFilterChange('earnings_score', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.earnings_score.max}
                  onChange={(e) => handleFilterChange('earnings_score', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Transcripts Score Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Transcripts Score</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.transcripts_score.min}
                  onChange={(e) => handleFilterChange('transcripts_score', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.transcripts_score.max}
                  onChange={(e) => handleFilterChange('transcripts_score', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Innovation Score Filter */}
            <div>
              <label className="block text-sm font-medium text-text-100 mb-2">Innovation Score</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Min"
                  value={filters.innovation_score.min}
                  onChange={(e) => handleFilterChange('innovation_score', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Max"
                  value={filters.innovation_score.max}
                  onChange={(e) => handleFilterChange('innovation_score', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Rank Change Filter */}
            <div>
              <div className="flex items-center mb-2">
                <label className="block text-sm font-medium text-text-100">Rank Change</label>
                <div className="relative">
                  <button
                    type="button"
                    className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTooltip('rankChange');
                    }}
                    onMouseEnter={() => setActiveTooltip('rankChange')}
                    onMouseLeave={() => setActiveTooltip(null)}
                    aria-label="Information about Rank Change"
                  >
                    <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                  {activeTooltip === 'rankChange' && (
                    <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 left-0 top-full mt-2 border border-bg-400">
                      {rankDescriptions.rankChange}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.rank_change.min}
                  onChange={(e) => handleFilterChange('rank_change', 'min', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.rank_change.max}
                  onChange={(e) => handleFilterChange('rank_change', 'max', e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Sector Filter */}
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-text-100 mb-2">Sectors</label>
              <div className="flex flex-wrap gap-2">
                {availableSectors.map(sector => (
                  <button
                    key={sector}
                    onClick={() => handleSectorToggle(sector)}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      filters.sectors.includes(sector)
                        ? 'bg-primary-600 text-white'
                        : 'bg-bg-300 text-text-200 hover:bg-bg-400'
                    }`}
                  >
                    {sector}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Results Summary */}
      {isApproved && (<div className="mt-6 bg-bg-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-text-200">
              {tickerSearch || tableSearch ? (
                <>
                  Showing <span className="font-semibold text-text-100">{sortedData.length}</span> stock{sortedData.length !== 1 ? 's' : ''} 
                  {tickerSearch && tableSearch ? ` matching "${tickerSearch}" and filtered by "${tableSearch}"` : 
                   tickerSearch ? ` matching "${tickerSearch}"` : 
                   ` filtered by "${tableSearch}"`}
                </>
              ) : (
                <>
                  Showing <span className="font-semibold text-text-100">{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)}</span> of <span className="font-semibold text-text-100">{totalCount.toLocaleString()}</span> stocks
                </>
              )}
            </p>
          </div>
          <div className="text-sm text-text-200">
            Data as of: {filteredData[0]?.as_of_date}
          </div>
        </div>
      </div>)}

      {/* Results Table */}
      {isApproved && (<div className="mt-8 flow-root">
        <div className="bg-bg-200 rounded-lg shadow-xl overflow-hidden">
          {/* Table Search - Filters visible results only */}
          <div className="px-4 pt-5 pb-3 border-b border-bg-300">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-text-100">Results</h3>
              <div className="relative w-64">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon className="h-4 w-4 text-text-300" aria-hidden="true" />
                </div>
                <input
                  type="text"
                  placeholder="Filter results..."
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value.toUpperCase())}
                  className="block w-full rounded-md border-0 bg-bg-100 py-1.5 pl-9 pr-8 text-sm text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-inset focus:ring-primary-100"
                />
                {tableSearch && (
                  <button
                    onClick={() => setTableSearch('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-2"
                  >
                    <XMarkIcon className="h-4 w-4 text-text-300 hover:text-text-100" />
                  </button>
                )}
              </div>
            </div>
            {tableSearch && (
              <p className="mt-2 text-xs text-text-300">
                Showing {sortedData.length} result{sortedData.length !== 1 ? 's' : ''} matching "{tableSearch}"
              </p>
            )}
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-bg-300 table-fixed">
              <thead>
                <tr>
                  <th 
                    onClick={() => requestSort('filter_rank')}
                    className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                  >
                    <div className="flex items-center">
                      Filter Rank
                      {sortConfig.key === 'filter_rank' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('rank_global')}
                    className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                  >
                    <div className="flex items-center">
                      Rank
                      {sortConfig.key === 'rank_global' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('ticker')}
                    className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                  >
                    <div className="flex items-center">
                      Ticker
                      {sortConfig.key === 'ticker' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('name')}
                    className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300" 
                    style={{width: '200px', maxWidth: '200px'}}
                  >
                    <div className="flex items-center">
                      Company
                      {sortConfig.key === 'name' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('rank_change')}
                    className="px-3 py-3.5 text-center text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                  >
                    <div className="flex items-center justify-center">
                      Rank Change
                      {sortConfig.key === 'rank_change' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('sector_name')}
                    className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                  >
                    <div className="flex items-center">
                      Sector
                      {sortConfig.key === 'sector_name' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('market_cap_usd')}
                    className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                  >
                    <div className="flex items-center justify-end">
                      Market Cap
                      {sortConfig.key === 'market_cap_usd' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('monthly_signal')}
                    className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                  >
                    <div className="flex items-center justify-end">
                      Signal Score
                      {sortConfig.key === 'monthly_signal' && (
                        <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative">
                    <div className="flex items-center justify-end">
                      Value
                      <div className="relative">
                        <button
                          type="button"
                          className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTooltip('value');
                          }}
                          onMouseEnter={() => setActiveTooltip('value')}
                          onMouseLeave={() => setActiveTooltip(null)}
                          aria-label="Information about Value"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {activeTooltip === 'value' && (
                          <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                            {signalDescriptions.value}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative">
                    <div className="flex items-center justify-end">
                      Momentum
                      <div className="relative">
                        <button
                          type="button"
                          className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTooltip('momentum');
                          }}
                          onMouseEnter={() => setActiveTooltip('momentum')}
                          onMouseLeave={() => setActiveTooltip(null)}
                          aria-label="Information about Momentum"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {activeTooltip === 'momentum' && (
                          <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                            {signalDescriptions.momentum}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative">
                    <div className="flex items-center justify-end">
                      Quality
                      <div className="relative">
                        <button
                          type="button"
                          className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTooltip('quality');
                          }}
                          onMouseEnter={() => setActiveTooltip('quality')}
                          onMouseLeave={() => setActiveTooltip(null)}
                          aria-label="Information about Quality"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {activeTooltip === 'quality' && (
                          <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                            {signalDescriptions.quality}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative">
                    <div className="flex items-center justify-end">
                      Low Vol
                      <div className="relative">
                        <button
                          type="button"
                          className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTooltip('lowvol');
                          }}
                          onMouseEnter={() => setActiveTooltip('lowvol')}
                          onMouseLeave={() => setActiveTooltip(null)}
                          aria-label="Information about Low Vol"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {activeTooltip === 'lowvol' && (
                          <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                            {signalDescriptions.lowvol}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative">
                    <div className="flex items-center justify-end">
                      Earnings
                      <div className="relative">
                        <button
                          type="button"
                          className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTooltip('earnings');
                          }}
                          onMouseEnter={() => setActiveTooltip('earnings')}
                          onMouseLeave={() => setActiveTooltip(null)}
                          aria-label="Information about Earnings"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {activeTooltip === 'earnings' && (
                          <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                            {signalDescriptions.earnings}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative">
                    <div className="flex items-center justify-end">
                      Transcripts
                      <div className="relative">
                        <button
                          type="button"
                          className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTooltip('transcripts');
                          }}
                          onMouseEnter={() => setActiveTooltip('transcripts')}
                          onMouseLeave={() => setActiveTooltip(null)}
                          aria-label="Information about Transcripts"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {activeTooltip === 'transcripts' && (
                          <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                            {signalDescriptions.transcripts}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 relative">
                    <div className="flex items-center justify-end">
                      Innovation
                      <div className="relative">
                        <button
                          type="button"
                          className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTooltip('innovation');
                          }}
                          onMouseEnter={() => setActiveTooltip('innovation')}
                          onMouseLeave={() => setActiveTooltip(null)}
                          aria-label="Information about Innovation"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        {activeTooltip === 'innovation' && (
                          <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                            {signalDescriptions.innovation}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-300">
                {sortedData.map((stock) => (
                  <tr 
                    key={stock.pk} 
                    className="hover:bg-bg-300/50 cursor-pointer"
                    onClick={() => {
                      const security = {
                        security_id: stock.security_id,
                        ticker: stock.ticker,
                        name: stock.name,
                        sector: stock.sector_name,
                        rank_global: stock.rank_global,
                        signal_weight: stock.monthly_signal
                      };
                      navigate(`/ticker/${stock.security_id}`, { 
                        state: { security }
                      });
                    }}
                  >
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                      {stock.filter_rank}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                      {stock.rank_global}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                      {stock.ticker}
                    </td>
                    <td className="px-3 py-4 text-sm text-text-200" style={{width: '200px', maxWidth: '200px'}}>
                      <div className="truncate overflow-hidden whitespace-nowrap" title={stock.name}>
                        {stock.name}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                      {stock.rank_change !== null && stock.rank_change !== undefined ? (
                        <div className={`inline-flex items-center ${
                          stock.rank_change > 0 ? 'text-green-400' : 
                          stock.rank_change < 0 ? 'text-red-400' : 
                          'text-text-200'
                        }`}>
                          {stock.rank_change > 0 && (
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414 6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {stock.rank_change < 0 && (
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {stock.rank_change === 0 ? '—' : Math.abs(stock.rank_change)}
                        </div>
                      ) : (
                        <span className="text-text-300">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                      {stock.sector_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatMarketCap(stock.market_cap_usd)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        stock.monthly_signal >= 80 ? 'bg-green-400/10 text-green-400' :
                        stock.monthly_signal >= 60 ? 'bg-green-300/10 text-green-300' :
                        stock.monthly_signal >= 40 ? 'bg-yellow-300/10 text-yellow-300' :
                        stock.monthly_signal >= 20 ? 'bg-red-300/10 text-red-300' :
                        'bg-red-400/10 text-red-400'
                      }`}>
                        {formatScore(stock.monthly_signal)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatScore(stock.value_score)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatScore(stock.momentum_score)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatScore(stock.quality_score)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatScore(stock.lowvol_score)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatScore(stock.earnings_score)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatScore(stock.transcripts_score)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                      {formatScore(stock.innovation_score)}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>)}

      {/* Pagination Controls */}
      {isApproved && totalCount > pageSize && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-text-200 bg-bg-200 hover:bg-bg-300 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage * pageSize >= totalCount}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-text-200 bg-bg-200 hover:bg-bg-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-text-200">
                Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{Math.ceil(totalCount / pageSize)}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-bg-200 text-sm font-medium text-text-200 hover:bg-bg-300 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage * pageSize >= totalCount}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-bg-200 text-sm font-medium text-text-200 hover:bg-bg-300 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Enterprise CTA Section */}
      {!isApproved && (
        <div className="mt-8">
          <div className="rounded-lg bg-gradient-to-r from-primary-100/10 to-accent-100/10 p-8">
            <div className="mx-auto max-w-md text-center">
              <h2 className="text-xl font-semibold text-text-100">
                Want Access to Advanced Research Tools?
              </h2>
              <p className="mt-2 text-text-200">
                Get access to our comprehensive research platform with advanced filtering, custom screening tools, 
                saved searches, and real-time analysis across over 25,000 securities.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="inline-flex items-center rounded-md bg-primary-100 px-3.5 py-2.5 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-100"
                >
                  Contact Us for Enterprise Access
                  <span className="ml-2" aria-hidden="true">&rarr;</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Filter Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-200 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-text-100 mb-4">Save Filter</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-100 mb-1">Filter Name</label>
                <input
                  type="text"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                  placeholder="My Custom Filter"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-100 mb-1">Description (Optional)</label>
                <textarea
                  value={filterDescription}
                  onChange={(e) => setFilterDescription(e.target.value)}
                  rows={3}
                  className="block w-full rounded-md border-0 bg-bg-300 py-1.5 text-text-100 placeholder:text-text-300 focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6"
                  placeholder="Describe your filter criteria..."
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowSaveModal(false)}
                className="rounded-md bg-bg-300 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-bg-400"
              >
                Cancel
              </button>
              <button
                onClick={saveFilter}
                disabled={!filterName.trim()}
                className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:bg-gray-600"
              >
                Save Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Filter Modal */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-200 rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-medium text-text-100 mb-4">Load Saved Filter</h3>
            
            <div className="border-b border-gray-700 mb-4">
              <nav className="-mb-px flex space-x-8">
                <button className="border-transparent text-text-200 hover:text-text-100 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm">
                  My Filters ({savedFilters.length})
                </button>
                <button className="border-transparent text-text-200 hover:text-text-100 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm">
                  Public Filters ({publicFilters.length})
                </button>
              </nav>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {/* User's Saved Filters */}
              <div className="space-y-3">
                {savedFilters.map((filter) => (
                  <div key={filter.id} className="flex items-center justify-between p-3 bg-bg-300 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-text-100">{filter.filter_name}</h4>
                      {filter.description && (
                        <p className="text-xs text-text-200 mt-1">{filter.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => loadFilter(filter.filter_criteria)}
                      className="rounded-md bg-primary-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                    >
                      Load
                    </button>
                  </div>
                ))}
                
                {/* Public Filters */}
                {publicFilters.map((filter) => (
                  <div key={filter.id} className="flex items-center justify-between p-3 bg-bg-300 rounded-lg border border-yellow-600">
                    <div>
                      <h4 className="text-sm font-medium text-text-100">
                        {filter.filter_name} 
                        <span className="ml-2 inline-flex items-center rounded-full bg-yellow-600 px-2 py-1 text-xs font-medium text-white">
                          Public
                        </span>
                      </h4>
                      {filter.description && (
                        <p className="text-xs text-text-200 mt-1">{filter.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => loadFilter(filter.filter_criteria)}
                      className="rounded-md bg-primary-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                    >
                      Load
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLoadModal(false)}
                className="rounded-md bg-bg-300 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-bg-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
              )}
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
} 